/** Tenant workspace sidebar nav — exported for tests + WorkspaceLayout. */

export interface NavSubItem {
  key: string
  labelKey: string
  path?: string
  featureKey?: string
}

export const WEBSITE_OPS_SUB_ITEMS: readonly NavSubItem[] = [
  { key: 'domains', labelKey: 'tenantNav.websiteOps.domains', featureKey: 'feature.custom_domain' },
  { key: 'publish', labelKey: 'tenantNav.websiteOps.publish', featureKey: 'feature.site_publish' },
]

/** Image library is always visible; processing children are entitlement-gated. */
export const IMAGES_SUB_ITEMS: readonly NavSubItem[] = [
  { key: 'assets', labelKey: 'tenantNav.images.assets', path: 'images' },
  {
    key: 'item-standard',
    labelKey: 'tenantNav.images.itemStandard',
    path: 'images/process',
    featureKey: 'feature.image_processing',
  },
  {
    key: 'flat-optimization',
    labelKey: 'tenantNav.images.flatOptimization',
    path: 'images/flat-optimization',
    featureKey: 'feature.flat_image_optimization',
  },
]

export const VIDEO_GEN_SUB_ITEMS: readonly NavSubItem[] = [
  { key: 'assets', labelKey: 'tenantNav.video.assets', path: 'video-generation/assets' },
  {
    key: 'projects',
    labelKey: 'tenantNav.video.projects',
    path: 'video-generation/projects',
    featureKey: 'feature.video_generation',
  },
  {
    key: 'main',
    labelKey: 'tenantNav.video.creation',
    path: 'video-generation',
    featureKey: 'feature.video_generation',
  },
  {
    key: 'continuation',
    labelKey: 'tenantNav.video.continuousGeneration',
    path: 'video-generation/continuation',
    featureKey: 'feature.video_continuation',
  },
]

/** VISTOERA_TENANT_AUDIO_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 §4 —
 * a single "音频资产" entry this round (task spec explicitly defers a
 * music-library / voice-template-library / sfx-library / recording-
 * library split into separate left-nav items to a future round). Always
 * visible — same "library view, not itself a gated generation
 * capability" precedent as `IMAGES_SUB_ITEMS`'s / `VIDEO_GEN_SUB_ITEMS`'s
 * own `assets` entries — no `featureKey` gate. */
export const AUDIO_SUB_ITEMS: readonly NavSubItem[] = [
  { key: 'assets', labelKey: 'tenantNav.audio.assets', path: 'audio-assets' },
]

/** VISTOERA_TENANT_TEXT_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 §5/§6
 * — a single top-level 文本 nav item with exactly one child (文本资产) for
 * this first version; deliberately NOT split into 创意库/剧本库/分镜库/文案库
 * sub-entries (those content types are still surfaced as Type tabs inside
 * the one unified page, not separate sidebar destinations). */
export const TEXT_SUB_ITEMS: readonly NavSubItem[] = [
  { key: 'assets', labelKey: 'tenantNav.text.assets', path: 'text-assets' },
]

export type ImagesSubItem = (typeof IMAGES_SUB_ITEMS)[number]
export type VideoGenSubItem = (typeof VIDEO_GEN_SUB_ITEMS)[number]
export type AudioSubItem = (typeof AUDIO_SUB_ITEMS)[number]
export type TextSubItem = (typeof TEXT_SUB_ITEMS)[number]

export function filterNavSubItems<T extends { featureKey?: string; key: string }>(
  items: readonly T[],
  hasFeature: (key: string) => boolean,
): T[] {
  return items.filter((item) => !item.featureKey || hasFeature(item.featureKey))
}

export function resolveSelectedImagesSubKey(pathname: string, imagesBase: string): string | undefined {
  if (!pathname.startsWith(imagesBase)) return undefined
  if (pathname.startsWith(`${imagesBase}/process`)) return 'images:item-standard'
  if (pathname.startsWith(`${imagesBase}/flat-optimization`)) return 'images:flat-optimization'
  return 'images:assets'
}

export function resolveSelectedVideoGenSubKey(pathname: string, videoGenBase: string): string | undefined {
  if (!pathname.startsWith(videoGenBase)) return undefined
  if (pathname.startsWith(`${videoGenBase}/projects`)) return 'video-generation:projects'
  if (pathname.startsWith(`${videoGenBase}/continuation`)) return 'video-generation:continuation'
  if (pathname.startsWith(`${videoGenBase}/assets`)) return 'video-generation:assets'
  if (pathname === videoGenBase || pathname.startsWith(`${videoGenBase}?`)) return 'video-generation:main'
  return 'video-generation:main'
}

export function resolveSelectedAudioSubKey(pathname: string, audioBase: string): string | undefined {
  if (!pathname.startsWith(audioBase)) return undefined
  return 'audio:assets'
}

export function resolveSelectedTextSubKey(pathname: string, textAssetsBase: string): string | undefined {
  if (!pathname.startsWith(textAssetsBase)) return undefined
  return 'text:assets'
}
